import { createContext, type ReactNode, useContext } from 'react';
import { createPortal } from 'react-dom';

/**
 * Контейнер шару для спливних елементів. Усередині модального <dialog> спливні елементи
 * мають рендеритися в сам діалог (верхній шар), інакше вони опиняться під ним і стануть inert.
 */
const LayerContext = createContext<HTMLElement | null>(null);

export function LayerProvider({ container, children }: { container: HTMLElement | null; children: ReactNode }) {
  return <LayerContext.Provider value={container}>{children}</LayerContext.Provider>;
}

export function useLayerContainer(): HTMLElement | null {
  return useContext(LayerContext);
}

export function Portal({ children }: { children: ReactNode }) {
  const container = useLayerContainer();
  if (typeof document === 'undefined') return null;
  return createPortal(children, container ?? document.body);
}

export type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'bottom' | 'top' | 'right-start' | 'left-start';

export type Rect = { top: number; left: number; bottom: number; right: number; width: number; height: number };

/** Обчислює позицію плаваючого елемента біля якоря з перевертанням і обмеженням вікном. */
export function computePosition(anchor: Rect, floating: { width: number; height: number }, placement: Placement, offset = 6, margin = 8) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let side = placement.split('-')[0] as 'bottom' | 'top' | 'right' | 'left';
  const align = (placement.split('-')[1] ?? 'center') as 'start' | 'end' | 'center';

  if (side === 'bottom' && anchor.bottom + offset + floating.height > vh - margin && anchor.top - offset - floating.height > margin) side = 'top';
  else if (side === 'top' && anchor.top - offset - floating.height < margin && anchor.bottom + offset + floating.height < vh - margin) side = 'bottom';
  else if (side === 'right' && anchor.right + offset + floating.width > vw - margin) side = 'left';
  else if (side === 'left' && anchor.left - offset - floating.width < margin) side = 'right';

  let top = 0;
  let left = 0;
  if (side === 'bottom' || side === 'top') {
    top = side === 'bottom' ? anchor.bottom + offset : anchor.top - offset - floating.height;
    left = align === 'start' ? anchor.left : align === 'end' ? anchor.right - floating.width : anchor.left + anchor.width / 2 - floating.width / 2;
  } else {
    left = side === 'right' ? anchor.right + offset : anchor.left - offset - floating.width;
    top = align === 'end' ? anchor.bottom - floating.height : anchor.top;
  }
  left = Math.max(margin, Math.min(left, vw - margin - floating.width));
  top = Math.max(margin, Math.min(top, vh - margin - floating.height));

  const originY = side === 'bottom' ? 'top' : side === 'top' ? 'bottom' : 'center';
  const originX = side === 'right' ? 'left' : side === 'left' ? 'right' : align === 'end' ? 'right' : align === 'start' ? 'left' : 'center';
  return { top, left, side, origin: `${originY} ${originX}` };
}

export function rectOf(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, bottom: r.bottom, right: r.right, width: r.width, height: r.height };
}

export function pointRect(x: number, y: number): Rect {
  return { top: y, left: x, bottom: y, right: x, width: 0, height: 0 };
}
